import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { CardStats, Pagination } from '@/components';
import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { Card } from '@/design-system/card';
import { SearchField } from '@/design-system/search-field';
import {
  SegmentedControl,
  SegmentedItem,
} from '@/design-system/segmented-control';
import { TableData, type TableDataColumn } from '@/design-system/table-data';
import { useUrlParam } from '@/hooks/urlState';

import { warehouseApi } from '../../api/warehouse.api';
import DownloadStockCsvModal from '../../components/DownloadStockCsvModal';
import { EmptyTable } from '../../components/ListShell';
import { HeaderActions } from '../../components/pickers';
import {
  codeCls,
  dateCls,
  Dash,
  HubRequired,
  LoadError,
  metaCls,
  nameCls,
  numCls,
  PageHeader,
  Section,
  StockTypeBadge,
} from '../../components/ui';
import { WarehouseFilterPanel } from '../../components/WarehouseFilterPanel';
import { STOCK_FILTERS } from '../../filterFields';
import { daysUntil, fmtDate, fmtDateTime, fmtQty } from '../../format';
import { useLoad } from '../../hooks/useLoad';
import {
  NO_TABS,
  PAGE_SIZE_OPTIONS,
  useListQuery,
} from '../../hooks/useListQuery';
import { fmtPct } from '../../occupancy';
import { STOCK_TYPE_META, STOCK_TYPES, STORAGE_TYPE_LABEL } from '../../status';
import type {
  StockMovement,
  WarehouseStock,
  WarehouseStockType,
} from '../../types';
import { useWarehouse } from '../../WarehouseContext';
import { WAREHOUSE_PATHS } from '../../paths';

/** Movements shown first, and added per "Muat lebih banyak". */
const MOVEMENT_PAGE = 20;
/** The ledger endpoint's newest-first cap. */
const MOVEMENT_MAX = 500;

const movementLink = (m: StockMovement): string | null =>
  m.refType === 'GOODS_RECEIPT'
    ? `${WAREHOUSE_PATHS.goodsReceipt}/${m.refID}`
    : m.refType === 'GOODS_ISSUE'
      ? `${WAREHOUSE_PATHS.picking}/${m.refID}`
      : m.refType === 'TRANSFER'
        ? `${WAREHOUSE_PATHS.storageTransfer}/${m.refID}`
        : m.refType === 'STOCK_OPNAME'
          ? `${WAREHOUSE_PATHS.stockOpname}/${m.refID}`
          : null;

const MOVEMENT_TONE = {
  INBOUND: { label: 'Inbound', status: 'success' },
  OUTBOUND: { label: 'Outbound', status: 'feature' },
  TRANSFER: { label: 'Transfer', status: 'information' },
  ADJUSTMENT: { label: 'Penyesuaian', status: 'warning' },
} as const;

/** The stock-type toggle: "Semua tipe" (no `stockType` sent) or one type. */
type StockTypeChoice = 'ALL' | WarehouseStockType;
const STOCK_TYPE_CHOICES: readonly StockTypeChoice[] = ['ALL', ...STOCK_TYPES];
const STOCK_TYPE_CHOICE_LABEL: Record<StockTypeChoice, string> = {
  ALL: 'Semua tipe',
  REGULAR: STOCK_TYPE_META.REGULAR.label,
  RETURN: STOCK_TYPE_META.RETURN.label,
};

/** One level of where stock sits: its name (+ the location's storage type). */
interface PlacementLevel {
  name: string;
  note?: string;
}

/**
 * Location / Section / Bin of a stock row. Stock can sit at any level, so a
 * level above the holding storage is simply absent (null): in a location →
 * no section, no bin; in a section → no bin.
 */
export const stockPlacement = (
  s: WarehouseStock
): Record<'location' | 'section' | 'bin', PlacementLevel | null> => {
  const type = s.location?.type;
  return {
    location: s.location
      ? {
          name: s.location.name,
          note:
            type && type !== 'STORAGE' ? STORAGE_TYPE_LABEL[type] : undefined,
        }
      : null,
    section: s.section ?? null,
    bin: s.storage.category === 'BIN' ? s.storage : null,
  };
};

const PlacementCell: React.FC<{ level: PlacementLevel | null }> = ({
  level,
}) =>
  level ? (
    <div className="max-w-[120px]">
      <p className={`${nameCls} truncate`} title={level.name}>
        {level.name}
      </p>
      {level.note && <p className={metaCls}>{level.note}</p>}
    </div>
  ) : (
    <Dash />
  );

const PLACEMENT_COLUMNS: TableDataColumn<WarehouseStock>[] = (
  [
    ['location', 'Location'],
    ['section', 'Section'],
    ['bin', 'Bin'],
  ] as const
).map(([level, title]) => ({
  key: level,
  title,
  render: (_v, s) => <PlacementCell level={stockPlacement(s)[level]} />,
}));

const STOCK_COLUMNS: TableDataColumn<WarehouseStock>[] = [
  {
    key: 'sku',
    title: 'SKU / item',
    render: (_v, s) => (
      <div className="max-w-[150px]">
        <p className={codeCls}>{s.sku}</p>
        <p className={`${metaCls} truncate`} title={s.itemName}>
          {s.itemName}
        </p>
      </div>
    ),
  },
  {
    key: 'client',
    title: 'Client',
    render: (_v, s) => {
      const name = s.client?.name ?? `Client #${s.clientID}`;
      return (
        <span className={`${nameCls} block max-w-[96px] truncate`} title={name}>
          {name}
        </span>
      );
    },
  },
  ...PLACEMENT_COLUMNS,
  {
    key: 'batchNumber',
    title: 'Batch number',
    render: (_v, s) => <span className={numCls}>{s.batchNumber}</span>,
  },
  {
    key: 'stockType',
    title: 'Tipe',
    render: (_v, s) => <StockTypeBadge type={s.stockType} />,
  },
  {
    key: 'expiredDate',
    title: 'Exp',
    render: (_v, s) => {
      if (!s.expiredDate) return <span className={dateCls}>Tanpa expiry</span>;
      const d = daysUntil(s.expiredDate);
      const note =
        d !== null && d <= 45 ? (d < 0 ? 'expired' : `${d} hari lagi`) : null;
      return (
        <div>
          <p className={dateCls}>{fmtDate(s.expiredDate)}</p>
          {note && (
            <p
              className={`${metaCls} ${d !== null && d < 0 ? 'text-(--state-error-base)' : ''}`}
            >
              {note}
            </p>
          )}
        </div>
      );
    },
  },
  {
    key: 'onHand',
    title: 'On hand',
    align: 'right',
    render: (_v, s) => <span className={numCls}>{fmtQty(s.qtyOnHand)}</span>,
  },
  {
    key: 'reserved',
    title: 'Reserved',
    align: 'right',
    render: (_v, s) => (
      <span
        className={`font-mono text-[12px] tabular-nums ${
          s.qtyReserved ? 'text-(--state-away-dark)' : 'text-text-soft-400'
        }`}
      >
        {fmtQty(s.qtyReserved)}
      </span>
    ),
  },
  {
    key: 'available',
    title: 'Available',
    align: 'right',
    render: (_v, s) => (
      <span className="font-mono text-[12px] font-semibold tabular-nums text-text-strong-950">
        {fmtQty(s.qtyAvailable)}
      </span>
    ),
  },
  {
    key: 'uom',
    title: 'UoM',
    render: (_v, s) => <span className={metaCls}>{s.uomName}</span>,
  },
];

const MOVEMENT_COLUMNS: TableDataColumn<StockMovement>[] = [
  {
    key: 'createdAt',
    title: 'Waktu',
    render: (_v, m) => (
      <span className={dateCls}>{fmtDateTime(m.createdAt)}</span>
    ),
  },
  {
    key: 'type',
    title: 'Tipe',
    render: (_v, m) => {
      const t = MOVEMENT_TONE[m.type] ?? MOVEMENT_TONE.TRANSFER;
      return (
        <Badge status={t.status} type="dot" size="sm">
          {t.label}
        </Badge>
      );
    },
  },
  {
    key: 'refCode',
    title: 'Dokumen',
    render: (_v, m) => {
      const to = movementLink(m);
      return to ? (
        <Link
          to={to}
          className={`${codeCls} text-accent hover:underline underline-offset-4`}
        >
          {m.refCode}
        </Link>
      ) : (
        <span className={codeCls}>{m.refCode}</span>
      );
    },
  },
  {
    key: 'sku',
    title: 'SKU',
    render: (_v, m) => <span className={codeCls}>{m.sku}</span>,
  },
  {
    key: 'batchNumber',
    title: 'Batch number',
    render: (_v, m) => <span className={numCls}>{m.batchNumber}</span>,
  },
  {
    key: 'bin',
    title: 'Storage',
    render: (_v, m) => {
      const name = m.storageToName ?? m.storageFromName;
      return name ? (
        <span
          className={`${nameCls} block max-w-[220px] truncate`}
          title={name}
        >
          {name}
        </span>
      ) : (
        <Dash />
      );
    },
  },
  {
    key: 'qtyDelta',
    title: 'Qty',
    align: 'right',
    render: (_v, m) => (
      <span
        className={`font-mono text-[12px] font-semibold tabular-nums ${
          m.qtyDelta > 0
            ? 'text-(--state-success-dark)'
            : 'text-text-strong-950'
        }`}
      >
        {m.qtyDelta > 0 ? '+' : ''}
        {fmtQty(m.qtyDelta)}
      </span>
    ),
  },
  {
    key: 'createdBy',
    title: 'Oleh',
    render: (_v, m) => <span className={metaCls}>{m.createdBy}</span>,
  },
];

const StockPage: React.FC = () => {
  const { hubID, ready, refreshToken } = useWarehouse();
  // URL-backed search / page / "Filter (N)" (client, storage chain, expiry).
  const q = useListQuery(NO_TABS, 'ALL', STOCK_FILTERS);
  const { api: filterApi, apiKey: filterKey, values, write } = q.filters;
  const search = q.search.committed || null;
  const { page, limit } = q;
  // URL-backed like the panel's filters; the list, tiles and CSV all follow it.
  const [stockType, setStockType] = useUrlParam<StockTypeChoice>(
    'stockType',
    'ALL',
    { allowed: STOCK_TYPE_CHOICES, resetPage: true }
  );
  const enabled = ready && hubID !== null;
  const stockFilter = useMemo(
    () => ({
      ...filterApi,
      ...(stockType === 'ALL' ? {} : { stockType }),
    }),
    [filterApi, stockType]
  );
  const [csvOpen, setCsvOpen] = useState(false);

  // The chosen storage belongs to one hub: a hub switch drops the chain.
  const prevHub = useRef(hubID);
  useEffect(() => {
    const before = prevHub.current;
    prevHub.current = hubID;
    if (before !== null && hubID !== null && before !== hubID) {
      if (values.locationID)
        write({ ...values, locationID: '', sectionID: '', binID: '' });
    }
  }, [hubID, values, write]);

  // Tiles, table and ledger follow the same filters.
  const summary = useLoad(
    (signal) =>
      warehouseApi.getStockSummary(
        { hubID, ...stockFilter, search },
        { signal }
      ),
    [hubID, filterKey, stockType, search, refreshToken],
    enabled
  );
  // "Stock quarantine" tile: the same filters, only stock held in quarantine
  // locations — the Link client portal's sixth tile, same wording.
  const quarantine = useLoad(
    (signal) =>
      warehouseApi.getStockSummary(
        { hubID, ...stockFilter, search, locationType: 'QUARANTINE' },
        { signal }
      ),
    [hubID, filterKey, stockType, search, refreshToken],
    enabled
  );
  // Hub occupancy tile: the hub-wide summary (a page of one bin is enough).
  const occupancy = useLoad(
    (signal) =>
      warehouseApi
        .listStorageOccupancy({ hubID: hubID as number, limit: 1 }, { signal })
        .then((r) => r.summary),
    [hubID, refreshToken],
    enabled
  );
  // Server paging + search; zero-qty batches never come back.
  const stocks = useLoad(
    (signal) =>
      warehouseApi.listStocks(
        { hubID: hubID as number, ...stockFilter, search, page, limit },
        { signal }
      ),
    [hubID, filterKey, stockType, search, page, limit, refreshToken],
    enabled
  );
  const ledgerClientID = filterApi.clientID as number | undefined;
  const ledgerStorageID = filterApi.storageID as string | undefined;
  // Newest first from the server; "Muat lebih banyak" raises the cap.
  // Per hub: switching hubs starts again from the first MOVEMENT_PAGE rows.
  const [moreFor, setMoreFor] = useState<{
    hubID: number | null;
    limit: number;
  }>({ hubID: null, limit: MOVEMENT_PAGE });
  const movementLimit = moreFor.hubID === hubID ? moreFor.limit : MOVEMENT_PAGE;
  const movements = useLoad(
    (signal) =>
      warehouseApi.listMovements(
        {
          hubID: hubID as number,
          clientID: ledgerClientID,
          storageID: ledgerStorageID,
          limit: movementLimit,
        },
        { signal }
      ),
    [hubID, ledgerClientID, ledgerStorageID, movementLimit, refreshToken],
    enabled
  );

  const pageRows = stocks.data?.data ?? [];
  const total = stocks.data?.pagination.total ?? 0;
  const latestMovements = movements.data ?? [];
  const moreMovements =
    latestMovements.length >= movementLimit && movementLimit < MOVEMENT_MAX;

  const s = summary.data;
  const quarantined = quarantine.data;
  const occ = occupancy.data;

  return (
    <div className="space-y-5 p-6">
      <PageHeader
        eyebrow="Warehouse · Dashboard"
        title="Stock"
        // Stock is per hub: no "Semua hub" that would only say "pilih hub dulu".
        actions={
          <HeaderActions allowAll={false}>
            <Button
              tone="neutral"
              style="stroke"
              onClick={() => setCsvOpen(true)}
              disabled={!enabled}
              data-testid="stock-csv-open"
              leftIcon={<i className="ri-download-2-line text-[14px]" />}
            >
              Download CSV
            </Button>
          </HeaderActions>
        }
      />

      {!enabled ? (
        <HubRequired />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 xl:grid-cols-7">
            <CardStats
              size="small"
              title="SKUs in stock"
              value={s ? fmtQty(s.skuCount) : '—'}
            />
            <CardStats
              size="small"
              title="Storages in use"
              value={s ? fmtQty(s.binsInUse) : '—'}
            />
            <CardStats
              size="small"
              title="Units on hand"
              value={s ? fmtQty(s.qtyOnHand) : '—'}
            />
            <CardStats
              size="small"
              title="Units reserved"
              value={s ? fmtQty(s.qtyReserved) : '—'}
            />
            <CardStats
              size="small"
              title="Stock available"
              value={s ? fmtQty(s.qtyAvailable) : '—'}
            />
            <CardStats
              size="small"
              title="Stock quarantine"
              value={quarantined ? fmtQty(quarantined.qtyOnHand) : '—'}
            />
            <Link
              to={WAREHOUSE_PATHS.storageOccupancy}
              data-testid="stock-occupancy-tile"
              className="rounded-lg focus-visible:outline-2 focus-visible:outline-accent"
            >
              <CardStats
                size="small"
                title="Utilisasi hub"
                value={occ ? fmtPct(occ.pctCbm) : '—'}
                subtitle={
                  occ ? `${fmtQty(occ.binHot)} bin penuh / over` : undefined
                }
                onClick={() => undefined}
                className="h-full"
              />
            </Link>
          </div>

          <Card padding="none">
            <div className="flex flex-col gap-3 border-b border-stroke-soft-200 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-center gap-2">
                <span id="stock-type-label" className={metaCls}>
                  Tipe stok
                </span>
                <SegmentedControl
                  size="sm"
                  value={stockType}
                  onValueChange={(v: string) => {
                    // A toggle group reports '' when the pressed item is pressed again.
                    const choice = STOCK_TYPE_CHOICES.find((c) => c === v);
                    if (choice) setStockType(choice);
                  }}
                  aria-labelledby="stock-type-label"
                  data-testid="stock-type"
                >
                  {STOCK_TYPE_CHOICES.map((c) => (
                    <SegmentedItem
                      key={c}
                      value={c}
                      data-testid={`stock-type-${c}`}
                    >
                      {STOCK_TYPE_CHOICE_LABEL[c]}
                    </SegmentedItem>
                  ))}
                </SegmentedControl>
              </div>
              <div className="flex items-center gap-2">
                <SearchField
                  size="sm"
                  value={q.search.value}
                  onChange={q.search.setValue}
                  debounceMs={0}
                  placeholder="Cari SKU, batch number, atau bin…"
                  className="w-64"
                />
                <WarehouseFilterPanel filters={q.filters} title="Filter stok" />
              </div>
            </div>
            <div className="p-4">
              {stocks.error ? (
                <LoadError
                  message={stocks.error.message}
                  onRetry={stocks.reload}
                />
              ) : (
                <>
                  <TableData
                    size="sm"
                    loading={stocks.loading}
                    data={pageRows}
                    columns={STOCK_COLUMNS}
                    emptyState={<EmptyTable title="Belum ada stok" />}
                  />
                  {total > 0 && (
                    <Pagination
                      currentPage={page}
                      totalItems={total}
                      itemsPerPage={limit}
                      onPageChange={q.setPage}
                      onPageSizeChange={q.setLimit}
                      pageSizeOptions={PAGE_SIZE_OPTIONS}
                    />
                  )}
                </>
              )}
            </div>
          </Card>

          <Section title="Riwayat pergerakan stok">
            {movements.error ? (
              <LoadError
                message={movements.error.message}
                onRetry={movements.reload}
              />
            ) : (
              <>
                <TableData
                  size="sm"
                  loading={movements.loading}
                  data={latestMovements}
                  columns={MOVEMENT_COLUMNS}
                  emptyState={<EmptyTable title="Belum ada pergerakan" />}
                />
                {moreMovements && (
                  <div className="mt-3 flex justify-center">
                    <Button
                      tone="neutral"
                      style="stroke"
                      size="xs"
                      loading={movements.loading}
                      onClick={() =>
                        setMoreFor({
                          hubID,
                          limit: Math.min(
                            MOVEMENT_MAX,
                            movementLimit + MOVEMENT_PAGE
                          ),
                        })
                      }
                      data-testid="movements-more"
                    >
                      Muat lebih banyak
                    </Button>
                  </div>
                )}
              </>
            )}
          </Section>
        </>
      )}

      {enabled && (
        <DownloadStockCsvModal
          open={csvOpen}
          onClose={() => setCsvOpen(false)}
          // The inventory CSV is the list as filtered and searched right now.
          inventory={{ hubID: hubID as number, ...stockFilter, search }}
        />
      )}
    </div>
  );
};

export default StockPage;
